
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Booking from "../../domain/booking/Booking";
import NewBookingDialog from "@/components/booking/NewBookingDialog.vue";
import Table from "@/domain/restaurant/Table";
import UtilService from "@/services/UtilService";
import BookingOverview from "@/components/booking/components/BookingOverview.vue";
import Room from "@/domain/restaurant/Room";
import BookingDialog from "@/components/booking/BookingDialog.vue";
import Restaurant from "@/domain/restaurant/Restaurant";
import ConfirmationDialog from "@/components/alert/ConfirmationDialog.vue";
import RemoteServices from "@/services/RemoteServices";
import BookingChangeTableDialog from "@/components/booking/components/BookingChangeTableDialog.vue";
import SearchBookingDialog from "@/components/booking/search/SearchBookingDialog.vue";
import TimeService from "@/services/TimeService";
import StatCard from "@/components/stats/StatCard.vue";
import Notice from "@/domain/restaurant/Notice";
import NoticeCard from "@/components/restaurant/notice/NoticeCard.vue";

interface itemGroup {
  name: string;
  items: any[];
}

@Component({
  components: {
    NoticeCard,
    StatCard,
    SearchBookingDialog,
    BookingChangeTableDialog,
    ConfirmationDialog,
    BookingDialog,
    BookingOverview,
    NewBookingDialog
  }
})
export default class BookingList extends Vue {
  @Prop({ default: () => [] })
  bookingList!: Booking[];

  @Prop({ default: null })
  notice!: Notice | null;

  @Prop({ default: false })
  loadingTable!: boolean;

  @Prop({ default: () => new Date() })
  selectedDate!: Date;

  @Prop({ default: true })
  groupByRoom!: boolean;

  @Prop({ default: false })
  showInactive!: boolean;

  @Prop({ default: true })
  showExpand!: boolean;

  @Prop({ default: false })
  enableSelection!: boolean;

  @Prop({ default: true })
  allowCreate!: boolean;

  grouped: string[] = [];

  @Prop({ default: false })
  sortByTableAndTime!: boolean;

  @Prop({ default: false })
  showIconTooCloseToMove!: boolean;

  @Prop({ default: false })
  preventSelectionDisplay!: boolean;

  expanded: Booking[] = [];

  headers = [
    {
      text: this.$t("booking.time"),
      value: "bookingDateTime",
      groupable: false,
      width: 100
    },
    {
      text: this.$t("booking.numberOfPeople"),
      value: "numberOfPeople",
      groupable: false
    },
    {
      text: this.$t("booking.customerName"),
      value: "customerName",
      groupable: false
    },

    {
      text: this.$t("booking.table.label"),
      value: "tableId",
      groupable: false,
      sort: (a: number, b: number) => this.sortByTableName(a, b)
    },

    {
      text: this.$t("booking.notes.label"),
      value: "notes",
      groupable: false,
      sortable: false
    },
    {
      text: this.$t("booking.status.label"),
      value: "status",
      groupable: false
    },

    {
      text: "",
      value: "data-table-expand",
      groupable: false,
      width: 0
    }
  ];

  @Prop({ default: null })
  selectedRestaurant!: Restaurant | null;

  @Prop({ default: () => [] })
  selectedBookings!: Booking[];

  newBookingDialog = false;

  loadingSeatedOrComplete = false;

  bookingDialog = false;

  @Prop({ default: false })
  searchBookingDialog = false;

  bookingChangeTableDialog = false;

  selectedBooking = new Booking();

  rooms: { [key: number]: Room } = {};

  tables: { [key: number]: Table } = {};

  bookingsByTable: { [key: number]: Booking[] } = {};

  loadingComputedInfo = false;
  async created() {
    this.loadingComputedInfo = true;
    this.$store.watch(
      (state, getters) => getters.getSelectedRestaurant,
      () => {
        this.getRoomDictionary();
        this.getTableDictionary();
      }
    );
    await this.getRoomDictionary();
    await this.getTableDictionary();
    this.toggleGroupByRoom();
    this.loadingComputedInfo = false;
  }

  get showSearchBookingDialog() {
    return this.searchBookingDialog;
  }

  set showSearchBookingDialog(val: boolean) {
    this.$emit("hideSearchBookingDialog", val);
  }

  get selectedBookingsList() {
    return this.selectedBookings;
  }

  get numberOfPeople() {
    let total = 0;
    for (let i = 0; i < this.bookingList.length; i++) {
      if(this.isActive(this.bookingList[i]))
        total += this.bookingList[i].numberOfPeople;
    }
    return total;
  }

  get numberOfBookings() {
    return this.filteredBookingList.length;
  }

  get seatedSortedList() {
    const list: Booking[] = [];
    for (let i = 0; i < this.bookingList.length; i++) {
      if (this.bookingList[i].isSeated() || this.bookingList[i].isComplete())
        list.push(this.bookingList[i]);
    }
    list.sort(Booking.sortBookingByStatusUpdateDateTime);
    return list;
  }

  set selectedBookingsList(newval: any) {
    console.log("changing selected list");
  }

  addToBookingsByTable(booking: Booking) {
    if (booking.tableId !== undefined) {
      const tableId: number = booking.tableId as number;
      const table = this.tables[tableId];
      const affectedTables = Table.getAllAffectedTableIds(table);
      for (let j = 0; j < affectedTables.length; j++) {
        let bookingArray: Booking[] = this.bookingsByTable[affectedTables[j]];
        if (bookingArray == null) bookingArray = [];
        bookingArray.push(booking);
        this.bookingsByTable[affectedTables[j]] = bookingArray;
      }
    }
  }

  hasBookingBefore(booking: Booking) {
    if (booking.tableId != null) {
      const bookings: Booking[] = this.bookingsByTable[booking.tableId];
      const timeString = booking.getBookingTime();
      if (bookings != null && bookings.length > 1) {
        for (let i = 0; i < bookings.length; i++) {
          if (timeString > bookings[i].getBookingTime()) return true;
        }
      }
    }
    return false;
  }

  defaultSorting() {
    return ["bookingDateTime"];
  }

  get filteredBookingList() {
    const toReturn: Booking[] = [];
    this.bookingsByTable = {};
    if (this.loadingComputedInfo) return [];
    for (let i = 0; i < this.bookingList.length; i++) {
      if (this.showInactive || this.isActive(this.bookingList[i])) {
        toReturn.push(this.bookingList[i]);
        this.addToBookingsByTable(this.bookingList[i]);
      }
    }
    return toReturn;
  }

  hasNotes(booking: Booking) {
    return !!booking.notes;
  }

  allowMarkAsSeated(booking: Booking): boolean {
    if (
      !booking.isSeated() &&
      !booking.isComplete() &&
      this.selectedRestaurant != null
    ) {
      return Restaurant.allowMarkAsSeated(
        this.selectedDate,
        this.selectedRestaurant
      );
    }
    return false;
  }

  async getTableDictionary() {
    const rest = await this.$store.getters.getSelectedRestaurant;
    if (rest != null) this.tables = UtilService.getTableDict(rest);
    else this.tables = {};
  }

  async getRoomDictionary() {
    const rest = await this.$store.getters.getSelectedRestaurant;
    if (rest != null) this.rooms = UtilService.getRoomDict(rest);
    else this.rooms = {};
  }

  getRoomColour(tableId: number) {
    const table = this.tables[tableId];
    let room;
    if (table != null) room = this.rooms[table.roomId];
    if (room != null) return Room.getRoomColourFromId(room.roomId);
    return "red";
  }

  createNewBookingShowDialog() {
    this.selectedBooking = new Booking();
    this.newBookingDialog = true;
  }

  addBooking(newBooking: Booking | null) {
    this.$emit("new-booking", newBooking);
    if (newBooking != null) {
      console.log("Adding booking to expanded");
      this.expanded.pop();
      this.expanded.push(newBooking);
    }
  }

  newBookingFromExisting(booking: Booking) {
    this.selectedBooking = booking;
    this.newBookingDialog = true;
  }

  updateBooking(newBooking: Booking) {
    for (let i = 0; i < this.bookingList.length; i++) {
      if (newBooking.bookingId === this.bookingList[i].bookingId) {
        Booking.updateBooking(this.bookingList[i], newBooking);
        return;
      }
    }
    console.log("Cant Find Booking To Update: BookingList:updateBooking");
  }

  openBookingDialog(booking: Booking) {
    this.selectedBooking = booking;
    this.bookingDialog = true;
  }

  isActive(booking: Booking) {
    return Booking.getStatusActive(booking.status);
  }

  getTableName(tableId: number | null): string | null {
    if (tableId != null && this.tables[tableId] != null)
      return this.tables[tableId].tableName;
    return this.$tc("booking.table.not.selected");
  }

  emitSelection(selection: any) {
    this.$emit("item-selected", selection);
  }

  emitAllSelected(selection: any) {
    this.$emit("toggle-select-all", selection);
  }

  @Watch("selectedDate")
  resetExpanded() {
    this.expanded = [];
  }

  @Watch("groupByRoom")
  public toggleGroupByRoom() {
    this.grouped.pop();
    if (this.groupByRoom) this.grouped.push("room");
  }

  sortByTableName(tableId1: number, tableId2: number) {
    const table1 = this.tables[tableId1];
    const table2 = this.tables[tableId2];
    return UtilService.stringComparatorAscending(
      table1 != null ? table1.tableName : "ZZZ",
      table2 != null ? table2.tableName : "ZZZ"
    );
  }

  groupBy(items: any[], groupBy: string[], groupDesc: boolean[]): itemGroup[] {
    const groupedItems: Record<string, any[]> = {};
    const toReturn: itemGroup[] = [];
    const roomNames: string[] = [];
    if (groupBy[0] === "room") {
      for (let i = 0; i < items.length; i++) {
        if (items[i].tableId != null) {
          const table = this.tables[items[i].tableId];
          if (table == null) return [];
          const room = this.rooms[table.roomId];
          if (groupedItems[room.roomName] == null) {
            groupedItems[room.roomName] = [];
            roomNames.push(room.roomName);
          }
          groupedItems[room.roomName].push(items[i]);
        } else {
          if (groupedItems[this.$tc("booking.no.table.selected")] == null) {
            groupedItems[this.$tc("booking.no.table.selected")] = [];
          }
          groupedItems[this.$tc("booking.no.table.selected")].push(items[i]);
        }
      }
      roomNames.sort(UtilService.stringComparatorAscending);
      for (let i = 0; i < roomNames.length; i++) {
        toReturn.push({
          name: roomNames[i],
          items: groupedItems[roomNames[i]]
        });
      }
    }
    return toReturn;
  }

  getBookingStatusIcon(booking: Booking) {
    if (this.selectedRestaurant != null) {
      return booking.getBookingStatusIcon().icon;
    }
    return "";
  }

  getBookingStatusIconColour(booking: Booking) {
    if (this.selectedRestaurant != null)
      return booking.getBookingStatusIcon().colour;
    return "";
  }

  getBookingTableAssignmentIcon(booking: Booking) {
    if (this.selectedRestaurant != null)
      return booking.getTableAssignmentIcon(
        this.selectedRestaurant,
        this.showIconTooCloseToMove
      ).icon;
    return "";
  }

  getBookingTableAssignmentIconColour(booking: Booking) {
    if (this.selectedRestaurant != null)
      return booking.getTableAssignmentIcon(
        this.selectedRestaurant,
        this.showIconTooCloseToMove
      ).colour;
    return "";
  }

  get confirmationDialog(): ConfirmationDialog {
    return this.$refs.ConfirmationDialog as ConfirmationDialog;
  }

  tableSuitableForBooking(booking: Booking) {
    return Table.suitableFor(
      this.tables[booking.tableId as number],
      booking.numberOfPeople
    );
  }

  async markBookingAsSeated(booking: Booking) {
    try {
      this.loadingSeatedOrComplete = true;
      const copy = await RemoteServices.markBookingAsSeated(booking.bookingId);
      await this.$store.dispatch(
        "success",
        this.$t("booking.edit.status.success")
      );
      this.updateBooking(copy);
    } catch (error) {
      await this.$store.dispatch("error", error.message);
    }
    this.loadingSeatedOrComplete = false;
  }

  async markBookingAsComplete(booking: Booking) {
    try {
      this.loadingSeatedOrComplete = true;
      const copy = await RemoteServices.markBookingAsComplete(
        booking.bookingId
      );
      await this.$store.dispatch(
        "success",
        this.$t("booking.edit.status.success")
      );
      this.updateBooking(copy);
    } catch (error) {
      await this.$store.dispatch("error", error.message);
    }
    this.loadingSeatedOrComplete = false;
  }

  showBookingChangeTableDialog(booking: Booking) {
    this.selectedBooking = booking;
    this.bookingChangeTableDialog = true;
  }

  async goToBooking(booking: Booking | null) {
    await this.$emit("selected-booking", booking);
    if (booking !== null) {
      this.expanded.pop();
      this.expanded.push(booking);

      if (
        !Booking.getStatusActive(booking.status) &&
        !Booking.isWaitingList(booking.status)
      )
        this.showInactive = true;
    }
  }

  seatedInLast15Mins(booking: Booking): boolean {
    const date = new Date();
    date.setMinutes(date.getMinutes() - 15);
    return TimeService.dateTimeIsAfterOrEqual(
      new Date(booking.statusUpdateDateTime),
      date
    );
  }

  getDisplaySeatedTime(booking: Booking) {
    return TimeService.getTimeFromISOString(booking.statusUpdateDateTime);
  }

  showNoticeDialog() {
    this.$emit("showNoticeDialog");
  }

  updateDate(newDate: Date){
    this.$emit("date-change", newDate);
  }
}
